// AuthService.js
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile,
  sendEmailVerification
} from 'firebase/auth';
import { 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { auth, db } from './firebase-config';

class AuthService {
  constructor() {
    this.currentUser = null;
    this.authStateCallbacks = [];
    
    // Listen for authentication state changes
    onAuthStateChanged(auth, (user) => {
      this.currentUser = user;
      this.authStateCallbacks.forEach(callback => callback(user));
    });
  }

  // Subscribe to auth state changes
  onAuthStateChange(callback) {
    this.authStateCallbacks.push(callback);
    
    // Return unsubscribe function
    return () => {
      this.authStateCallbacks = this.authStateCallbacks.filter(cb => cb !== callback);
    };
  }

  // Register new user
  async register(email, password, userData = {}) {
    try {
      // Create user account
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Update user profile
      if (userData.displayName) {
        await updateProfile(user, {
          displayName: userData.displayName
        });
      }

      // Send email verification
      await sendEmailVerification(user);

      // Create user document in Firestore
      await this.createUserDocument(user.uid, {
        email: user.email,
        displayName: userData.displayName || '',
        createdAt: serverTimestamp(),
        emailVerified: false,
        ...userData
      });

      return {
        success: true,
        user: user,
        message: 'Account created successfully. Please check your email for verification.'
      };

    } catch (error) {
      console.error('Registration error:', error);
      return {
        success: false,
        error: this.getErrorMessage(error.code)
      };
    }
  }

  // Sign in user
  async signIn(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Update last login time
      await this.updateUserDocument(user.uid, {
        lastLoginAt: serverTimestamp()
      });

      return {
        success: true,
        user: user,
        message: 'Signed in successfully'
      };

    } catch (error) {
      console.error('Sign in error:', error);
      return {
        success: false,
        error: this.getErrorMessage(error.code)
      };
    }
  }

  // Sign out user
  async signOut() {
    try {
      await signOut(auth);
      return {
        success: true,
        message: 'Signed out successfully'
      };
    } catch (error) {
      console.error('Sign out error:', error);
      return {
        success: false,
        error: 'Failed to sign out'
      };
    }
  }

  // Send password reset email
  async resetPassword(email) {
    try {
      await sendPasswordResetEmail(auth, email);
      return {
        success: true,
        message: 'Password reset email sent'
      };
    } catch (error) {
      console.error('Password reset error:', error);
      return {
        success: false,
        error: this.getErrorMessage(error.code)
      };
    }
  }

  // Get current user
  getCurrentUser() {
    return this.currentUser;
  }

  // Check if user is authenticated
  isAuthenticated() {
    return this.currentUser !== null;
  }

  // Check if email is verified
  isEmailVerified() {
    return this.currentUser?.emailVerified || false;
  }

  // Create user document in Firestore
  async createUserDocument(uid, userData) {
    try {
      const userDoc = doc(db, 'users', uid);
      await setDoc(userDoc, userData);
      return { success: true };
    } catch (error) {
      console.error('Error creating user document:', error);
      return { success: false, error: error.message };
    }
  }

  // Update user document in Firestore
  async updateUserDocument(uid, userData) {
    try {
      const userDoc = doc(db, 'users', uid);
      await updateDoc(userDoc, userData);
      return { success: true };
    } catch (error) {
      console.error('Error updating user document:', error);
      return { success: false, error: error.message };
    }
  }

  // Get user document from Firestore
  async getUserDocument(uid) {
    try {
      const userDoc = doc(db, 'users', uid);
      const docSnap = await getDoc(userDoc);
      
      if (docSnap.exists()) {
        return { success: true, data: docSnap.data() };
      } else {
        return { success: false, error: 'User document not found' };
      }
    } catch (error) {
      console.error('Error getting user document:', error);
      return { success: false, error: error.message };
    }
  }

  // Save user financial data
  async saveFinancialData(data) {
  if (!this.currentUser) {
    console.error("No authenticated user");
    return { success: false, error: 'User not authenticated' };
  }

  try {
    console.log("Attempting to save financial data for user:", this.currentUser.uid);
    
    // Create or update the financial document
    const financialDoc = doc(db, 'financial_data', this.currentUser.uid);
    
    // First try to get the document to see if it exists
    const docSnap = await getDoc(financialDoc);
    
    if (docSnap.exists()) {
      console.log("Document exists, updating it");
      await updateDoc(financialDoc, {
        ...data,
        updatedAt: serverTimestamp()
      });
    } else {
      console.log("Document doesn't exist, creating it");
      await setDoc(financialDoc, {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        userId: this.currentUser.uid
      });
    }

    console.log("Successfully saved financial data");
    return { success: true };
  } catch (error) {
    console.error('Error saving financial data:', error);
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);
    return { success: false, error: error.message };
  }
}

  // Load user financial data
  async loadFinancialData() {
    if (!this.currentUser) {
      return { success: false, error: 'User not authenticated' };
    }

    try {
      const financialDoc = doc(db, 'financial_data', this.currentUser.uid);
      const docSnap = await getDoc(financialDoc);
      
      if (docSnap.exists()) {
        return { success: true, data: docSnap.data() };
      } else {
        // Return default data structure if no data exists
        return { success: true, data: this.getDefaultFinancialData() };
      }
    } catch (error) {
      console.error('Error loading financial data:', error);
      return { success: false, error: error.message };
    }
  }

  // Get default financial data structure
  getDefaultFinancialData() {
    return {
      incomeData: [
        { month: 'Jan', salary: 0, business: 0, rental: 0, crypto: 0, affiliate: 0, total: 0 },
        { month: 'Feb', salary: 0, business: 0, rental: 0, crypto: 0, affiliate: 0, total: 0 },
        { month: 'Mar', salary: 0, business: 0, rental: 0, crypto: 0, affiliate: 0, total: 0 }
      ],
      expenseData: [
        { month: 'Jan', rent: 0, utilities: 0, insurance: 0, groceries: 0, entertainment: 0, travel: 0, other: 0, total: 0 },
        { month: 'Feb', rent: 0, utilities: 0, insurance: 0, groceries: 0, entertainment: 0, travel: 0, other: 0, total: 0 },
        { month: 'Mar', rent: 0, utilities: 0, insurance: 0, groceries: 0, entertainment: 0, travel: 0, other: 0, total: 0 }
      ],
      goals: [
        { name: 'FIRE Target', target: 1000000, current: 0, progress: 0 },
        { name: 'Emergency Fund', target: 25000, current: 0, progress: 0 },
        { name: 'Investment Target', target: 50000, current: 0, progress: 0 }
      ],
      debts: [
        { name: 'Credit Card', original: 0, current: 0, paidOff: 0 },
        { name: 'Student Loan', original: 0, current: 0, paidOff: 0 },
        { name: 'Car Loan', original: 0, current: 0, paidOff: 0 },
        { name: 'Mortgage', original: 0, current: 0, paidOff: 0 }
      ],
      netWorth: {
        assets: 0,
        liabilities: 0,
        netWorth: 0,
        fireGoal: 1000000,
        progress: 0
      },
      emergencyFund: {
        monthlyExpenses: 0,
        threeMonthGoal: 0,
        sixMonthGoal: 0,
        current: 0,
        progress: 0
      },
      investments: []
    };
  }

  // Convert Firebase error codes to user-friendly messages
  getErrorMessage(errorCode) {
    const errorMessages = {
      'auth/user-not-found': 'No account found with this email address.',
      'auth/wrong-password': 'Incorrect password.',
      'auth/email-already-in-use': 'An account with this email already exists.',
      'auth/weak-password': 'Password should be at least 6 characters.',
      'auth/invalid-email': 'Please enter a valid email address.',
      'auth/user-disabled': 'This account has been disabled.',
      'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
      'auth/network-request-failed': 'Network error. Please check your connection.',
      'auth/requires-recent-login': 'Please sign in again to perform this action.'
    };

    return errorMessages[errorCode] || 'An unexpected error occurred. Please try again.';
  }
}

// Create and export a singleton instance
const authService = new AuthService();
export default authService;